import { apiRequest } from './client';

/** White-labelling (SRS #38/#102): the effective brand for the shell and the workspace / platform editors. */
const BASE = '/api/v1/branding';
const TOKEN_KEY = 'policyora_token';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';

function authOpts(extra: RequestInit = {}): RequestInit {
  const token = localStorage.getItem(TOKEN_KEY);
  return { ...extra, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(extra.headers ?? {}) } };
}

export interface Brand {
  source: 'workspace' | 'platform' | 'default';
  owner_user_id: number | null;
  brand_name: string; tagline: string; primary_color: string; accent_color: string;
  logo_url: string | null; watermark_enabled: boolean; watermark_text: string; footer_text: string; platform_credit: boolean;
  updated_at: string | null;
}
export interface EffectiveBrand extends Brand { can_customise: boolean }
export interface StoredBranding {
  brand_name: string | null; tagline: string | null; primary_color: string | null; accent_color: string | null;
  watermark_enabled: boolean | null; watermark_text: string | null; footer_text: string | null; platform_credit: boolean;
  logo_url: string | null; logo_filename: string | null; updated_at: string | null; updated_by: string | null;
}
export type BrandingPatch = Partial<Omit<StoredBranding, 'logo_url' | 'logo_filename' | 'updated_at' | 'updated_by'>>;
export interface WorkspaceBrandingView { branding: StoredBranding; effective: Brand; is_owner: boolean; limits: Record<string, number> }
export interface PlatformBrandingView { branding: StoredBranding; effective: Brand; defaults: Brand; limits: Record<string, number> }
export interface CustomisedWorkspace { owner_user_id: number; email: string; organisation: string | null; brand_name: string | null; has_logo: boolean; allowed: boolean; updated_at: string | null; updated_by: string | null }

/** Logo URLs from the API are server-relative; make them absolute for <img>. */
export const logoSrc = (url: string | null): string | null => (url ? (url.startsWith('http') ? url : `${API_BASE_URL}${url}`) : null);

export const fetchEffectiveBrand = () => apiRequest<EffectiveBrand>(`${BASE}/effective`, authOpts());
export const fetchWorkspaceBranding = () => apiRequest<WorkspaceBrandingView>(`${BASE}/workspace`, authOpts());
export const updateWorkspaceBranding = (patch: BrandingPatch) => apiRequest<{ branding: StoredBranding; effective: Brand }>(`${BASE}/workspace`, authOpts({ method: 'PUT', body: JSON.stringify(patch) }));
export const resetWorkspaceBranding = () => apiRequest<{ removed: boolean }>(`${BASE}/workspace`, authOpts({ method: 'DELETE' }));
export const removeWorkspaceLogo = () => apiRequest<{ removed: boolean }>(`${BASE}/workspace/logo`, authOpts({ method: 'DELETE' }));

async function upload(path: string, file: File): Promise<{ logo_url: string }> {
  const token = localStorage.getItem(TOKEN_KEY);
  const fd = new FormData(); fd.append('file', file);
  const res = await fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd });
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try { const j = await res.json(); msg = typeof j.detail === 'string' ? j.detail : j.detail?.message ?? msg; } catch { /* keep */ }
    throw new Error(msg);
  }
  return res.json();
}
export const uploadWorkspaceLogo = (file: File) => upload(`${BASE}/workspace/logo`, file);

export const adminPlatformBranding = () => apiRequest<PlatformBrandingView>('/api/v1/admin/branding/platform', authOpts());
export const adminUpdatePlatformBranding = (patch: BrandingPatch) => apiRequest<{ branding: StoredBranding; effective: Brand }>('/api/v1/admin/branding/platform', authOpts({ method: 'PUT', body: JSON.stringify(patch) }));
export const adminUploadPlatformLogo = (file: File) => upload('/api/v1/admin/branding/platform/logo', file);
export const adminRemovePlatformLogo = () => apiRequest<{ removed: boolean }>('/api/v1/admin/branding/platform/logo', authOpts({ method: 'DELETE' }));
export const adminCustomisedWorkspaces = () => apiRequest<{ workspaces: CustomisedWorkspace[] }>('/api/v1/admin/branding/workspaces', authOpts());
export const adminRemoveWorkspaceBranding = (ownerId: number) => apiRequest<{ removed: boolean }>(`/api/v1/admin/branding/workspaces/${ownerId}`, authOpts({ method: 'DELETE' }));
